import { Transition } from '@headlessui/react';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/outline';
import { XIcon } from '@heroicons/react/solid';
import { Fragment, useEffect, useState } from 'react';

const icons = {
  info: <InformationCircleIcon className="h-6 w-6 text-blue-500" aria-hidden="true" />,
  success: <CheckCircleIcon className="h-6 w-6 text-green-500" aria-hidden="true" />,
  warning: <ExclamationCircleIcon className="h-6 w-6 text-yellow-500" aria-hidden="true" />,
  error: <XCircleIcon className="h-6 w-6 text-red-500" aria-hidden="true" />,
};

export const Notification = ({ notification: { id, type, title, message }, onDismiss }) => {
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    // Animate progress bar over 4 seconds
    const interval = setInterval(() => {
      setProgress((p) => p - 1);
    }, 40);

    // Auto dismiss after 4 seconds
    const timeout = setTimeout(() => onDismiss(id), 4000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [id, onDismiss]);

  return (
    <div className="w-full flex flex-col items-center space-y-4 sm:items-end">
      <Transition
        show={true}
        as={Fragment}
        enter="transform ease-out duration-300"
        enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
        enterTo="translate-y-0 opacity-100 sm:translate-x-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div
          className="
          max-w-sm w-full
          bg-white/90 backdrop-blur
          shadow-xl rounded-full
          pointer-events-auto
          ring-1 ring-black/10
          overflow-hidden
        "
        >
          <div className="p-4 pl-5 pr-4" role="alert" aria-label={title}>
            <div className="flex items-center">
              {/* Icon */}
              <div className="flex-shrink-0 mr-3">{icons[type]}</div>

              {/* Text */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900">{title}</p>
                {message && <p className="text-xs text-gray-600 truncate">{message}</p>}
              </div>

              {/* Close button */}
              <button
                className="
                  ml-3 flex-shrink-0 rounded-full
                  text-gray-400 hover:text-gray-600
                  focus:outline-none
                "
                onClick={() => onDismiss(id)}
              >
                <XIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Timeout Progress Bar */}
          <div className="h-1 bg-gray-200 w-full">
            <div
              className="
                h-full
                bg-gradient-to-r from-green-500 to-green-400
                transition-all duration-75 ease-linear
              "
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      </Transition>
    </div>
  );
};
